import {
  ACCEPTED_FILE_TYPES,
  FILE_TYPE,
  fileType,
  IFile,
  IFileAndCaption,
  IMediaDraft,
  IPhotoFile,
  IVideoFile,
  MediaDraft,
  UPLOAD_CONFIG,
} from "@home-health-notify/domain";
import { ImmutableChat } from "@home-health-notify/domain/build/chat/ImmutableChat";
import {
  UploadProgress,
  useMediaMessageSend,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  toAudioFile,
  toDocFile,
  toPhotoFile,
  toVideoFile,
} from "../chats/MediaFiles";
import { fileValidator, getDropZoneBaseConfig } from "../common/DropZoneConfig";
import config from "../utils/Config";
import { uploadToCloudStorage } from "../utils/WebCloudStoargeUploader";
import { userTracking } from "../analytics/userTracking";

export const useFileUpload = (chat: ImmutableChat) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  const fileTooLargeMessage = t("preview.fileSizeError", {
    ns: "message",
    maxFileSize: config.fileSizeLimitBytes / (1024 * 1024),
  });
  const [mediaDrafts, setMediaDrafts] = useState<IMediaDraft[]>([]);
  const [previewDraft, setPreviewDraft] = useState<IMediaDraft | undefined>(
    undefined,
  );

  const [isDropAccepted, setDropAccepted] = useState(false);
  const [isThumbnailCreated, setThumbnailCreated] = useState(false);
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<IFileAndCaption[]>([]);
  const [showBulkUploadConfirm, setShowBulkUploadConfirm] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const [fileProgressStatus, setFileProgressStatus] =
    useState<UploadProgress>();

  const [isUploadFailed, setUploadError] = useState(false);

  const dropZoneErrorCallback = (errorMessage: string) => {
    showError(errorMessage);
  };

  const webConfig = UPLOAD_CONFIG.web;

  const processFiles = async (fileWithCaptions: IFileAndCaption[]) => {
    if (!validateTotalSize(fileWithCaptions)) return;
    if (fileWithCaptions.length > webConfig.BULK_UPLOAD_CONFIRM_THRESHOLD) {
      setPendingFiles(fileWithCaptions);
      setShowBulkUploadConfirm(true);
      return;
    }
    await doUploadFiles(fileWithCaptions);
  };

  const onConfirmBulkUpload = async () => {
    setShowBulkUploadConfirm(false);
    await doUploadFiles(pendingFiles);
    setPendingFiles([]);
  };

  const onCancelBulkUpload = () => {
    setShowBulkUploadConfirm(false);
    setPendingFiles([]);
  };

  const doUploadFiles = async (fileWithCaptions: IFileAndCaption[]) => {
    setIsProcessingFiles(true);
    setDropAccepted(true);

    for (const fileWithCaption of fileWithCaptions) {
      const fileWithPath = Object.assign(fileWithCaption.file, {
        filePath: URL.createObjectURL(fileWithCaption.file),
      });

      let mediaFile: IFile;
      const type = fileType(fileWithPath);
      if (type === FILE_TYPE.IMAGE) {
        mediaFile = toPhotoFile(fileWithPath);
        if (!validatePhotoFile(mediaFile)) continue;
      } else if (type === FILE_TYPE.AUDIO) {
        mediaFile = await toAudioFile(fileWithPath);
      } else if (type === FILE_TYPE.VIDEO) {
        mediaFile = await toVideoFile(fileWithPath);
        onThumbnailCreated(true);
      } else if (type === FILE_TYPE.DOC) {
        mediaFile = toDocFile(fileWithPath);
      } else {
        throw new Error(`Unsupported file: ${fileWithPath.name}`);
      }
      const defaultCaption = mediaFile.isDoc() ? mediaFile.name : "";
      const mediaDraft = new MediaDraft(
        mediaFile,
        fileWithCaption.caption || defaultCaption,
      );

      setMediaDrafts(prevMediaDrafts => {
        return [...prevMediaDrafts, mediaDraft];
      });

      setPreviewDraft(mediaDraft);
    }
    setIsProcessingFiles(false);
  };

  const pasteFiles = async (fileWithCaptions: IFileAndCaption[]) => {
    const validator = fileValidator(fileTooLargeMessage);

    for (let index = 0; index < fileWithCaptions.length; index++) {
      const file = fileWithCaptions[index].file;
      const error = validator(file);
      if (!!error?.code) {
        showError(error.message);
        return;
      }

      if (!Object.keys(ACCEPTED_FILE_TYPES).includes(file.type)) {
        throw new Error(`Unsupported file: ${file.name}`);
      }
    }
    await processFiles(fileWithCaptions);
  };

  const { getRootProps, isDragActive, open, getInputProps } = useDropzone({
    ...getDropZoneBaseConfig({ errorCallback: dropZoneErrorCallback, t }),
    noClick: true,
    noKeyboard: true,
    accept: ACCEPTED_FILE_TYPES,
    onDrop: async (acceptedFiles: File[]) => {
      await processFiles(
        acceptedFiles.map(file => ({
          file,
        })),
      );
    },
    multiple: true,
    onDropAccepted: () => setDropAccepted(true),
    onDragEnter: () => setDropAccepted(false),
    onError: error => {
      showError(error.message);
    },
  });

  const onThumbnailCreated = (isThumbnailCreated: boolean) => {
    setThumbnailCreated(isThumbnailCreated);
  };

  const resetThumbnailFlag = () => {
    setThumbnailCreated(false);
  };

  useEffect(() => {
    return () => {
      mediaDrafts.forEach(md => URL.revokeObjectURL(md.mediaFile.filePath));
      mediaDrafts
        .filter(md => md.mediaFile.isVideo())
        .forEach(async file => {
          const videoFile = file as unknown as IVideoFile;
          if (videoFile && typeof videoFile.getThumbnail === "function") {
            const thumbnail = await videoFile.getThumbnail?.();
            if (thumbnail) {
              URL.revokeObjectURL(thumbnail.filePath);
            }
          }
        });
      setMediaDrafts([]);
      setPreviewDraft(undefined);
      setDropAccepted(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.chatInfo.id]);

  const validatePhotoFile = (photo: IPhotoFile) => {
    if (photo.isImage() && photo.size > config.fileSizeLimitBytes) {
      showError(tNs("preview.imageSizeError"));
      return false;
    }

    return true;
  };

  const validateTotalSize = (fileWithCaptions: IFileAndCaption[]) => {
    const totalSize = fileWithCaptions.reduce((sum, f) => sum + f.file.size, 0);
    if (totalSize > webConfig.MAX_TOTAL_UPLOAD_SIZE_BYTES) {
      showError(
        t("preview.totalSizeError", {
          ns: "message",
          maxSize: webConfig.MAX_TOTAL_UPLOAD_SIZE_BYTES / (1024 * 1024),
        }),
      );
      return false;
    }
    return true;
  };

  const showError = (message: string) => {
    enqueueSnackbar(message, {
      autoHideDuration: 3000,
    });
  };

  const openFileDialog = () => {
    open();
  };

  const onRemoveFile = (mediaDraft: IMediaDraft) => {
    let itemIndex = mediaDrafts.indexOf(mediaDraft);
    let updatedDrafts = mediaDrafts.filter((e, i) => i !== itemIndex);
    setMediaDrafts(updatedDrafts);
    if (updatedDrafts.length === 0) {
      setDropAccepted(false);
    }
    if (mediaDraft === previewDraft) {
      const lastDraft = updatedDrafts[itemIndex - 1]
        ? updatedDrafts[itemIndex - 1]
        : updatedDrafts[0];
      setPreviewDraft(lastDraft);
    }
  };

  const onSetPreview = (mediaDraft: IMediaDraft) => {
    setPreviewDraft(mediaDraft);
  };

  const onUpload = async () => {
    const mediaDraftsToSend = [...mediaDrafts];
    onClosePreview();
    await onSend(mediaDraftsToSend);
  };

  const onSetCaption = (mediaDraft: IMediaDraft, caption: string) => {
    const index = mediaDrafts.findIndex(
      md => md.mediaFile === mediaDraft.mediaFile,
    );
    if (index === -1) return;
    setMediaDrafts([
      ...mediaDrafts.slice(0, index),
      Object.assign({}, mediaDrafts[index], { text: caption }),
      ...mediaDrafts.slice(index + 1),
    ]);
  };

  const onUploadError = (file: IFile, _error: unknown) => {
    setUploadError(true);
    if ((_error as any)?.status === 413) {
      const message = (_error as any).response.data.message;
      showError(message + " " + file.name);
      return;
    }
    showError(tNs("preview.uploadError") + file.name);
  };

  const onUploadProgress = (progress: UploadProgress) => {
    setFileProgressStatus(progress);
  };

  const onClosePreview = () => {
    setMediaDrafts([]);
    setPreviewDraft(undefined);
    setDropAccepted(false);
    setFileProgressStatus(undefined);
  };

  const createSendMediaResponseCallback = useCallback(() => {
    const trackingId = `media_${Date.now()}`;
    userTracking.trackMessageSendStart(trackingId);

    return (responseData: any, socketPayload?: any) => {
      const chatType = socketPayload?.data?.chatType;
      const messageType = socketPayload?.data?.contentType;

      if (!responseData.statusCode || responseData.statusCode === 200) {
        userTracking.trackMessageSendComplete(
          trackingId,
          chatType,
          messageType,
        );
      } else {
        userTracking.trackMessageSendFailed(
          trackingId,
          `status_${responseData.statusCode}`,
          chatType,
        );
      }
    };
  }, []);

  const { onSend } = useMediaMessageSend(
    onUploadProgress,
    onClosePreview,
    onUploadError,
    uploadToCloudStorage,
    "web",
    chat,
    createSendMediaResponseCallback,
  );

  return {
    mediaDrafts,
    getRootProps,
    isDragActive,
    isDropAccepted,
    setDropAccepted,
    fileProgressStatus,
    openFileDialog,
    onRemoveFile,
    previewDraft,
    onSetPreview,
    onClosePreview,
    onUpload,
    isUploadFailed,
    onSetCaption,
    isThumbnailCreated,
    resetThumbnailFlag,
    getInputProps,
    isProcessingFiles,
    pasteFiles,
    showBulkUploadConfirm,
    pendingFilesCount: pendingFiles.length,
    onConfirmBulkUpload,
    onCancelBulkUpload,
  };
};
